import {
  Column,
  ImageThumbnail,
  ImageThumbnailSizesEnum,
  TablePaginated,
  TablePaginatedOptions,
  TrashDeleteIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { useStateOptions } from '@/hooks/useStateOptions';
import { useTableMultiselect } from '@/hooks/useTableMultiselect';
import { State } from '@/types';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useFeaturedPeopleFilters } from '../../stores/featured-people-filters';
import { TFeaturedPeopleType } from '../../types';

interface ParentFeaturedPeopleTableProps {
  items: TFeaturedPeopleType[];
  totalItems: number;
  isFetching?: boolean;
  activeItem: TFeaturedPeopleType | null;
  onUpdate?: (featuredPeople: TFeaturedPeopleType) => void;
  onDuplicate?: (featuredPeople: TFeaturedPeopleType) => void;
  onDelete?: (featuredPeople: TFeaturedPeopleType) => void;
  onSelect?: (featuredPeople: TFeaturedPeopleType) => void;
  onMultiDelete?: (featuredPeople: TFeaturedPeopleType[]) => void;
}

export function FeaturedPeopleTable({
  items,
  totalItems,
  isFetching,
  activeItem,
  onUpdate,
  onDelete,
  onSelect,
  onMultiDelete,
}: ParentFeaturedPeopleTableProps) {
  const { t } = useTranslation();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useFeaturedPeopleFilters();
  const userOptions = useUserOptions();
  const stateOptions = useStateOptions();

  const { checkboxColumn, multiSelectionButtonProps } = useTableMultiselect<TFeaturedPeopleType>({
    itemsCount: items.length,
    actions: [
      {
        icon: TrashDeleteIcon,
        onClick: selection => {
          onMultiDelete?.(selection.selectedItems);
        },
        tooltip: t('Eliminar'),
      },
    ],
  });

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    filters: [
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: stateOptions,
          allOptionLabel: t('Cualquier estado'),
        }),
        onChange: value => {
          setFilters({
            stateIn: [value as unknown as State],
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por nombre o descripción'),
    multiselect: multiSelectionButtonProps,
  };

  const columns: Column<TFeaturedPeopleType>[] = useMemo(
    () => [
      checkboxColumn,
      {
        id: 'name',
        sortable: true,
        title: t('Nombre'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return (
            <div className="flex items-center gap-2">
              <ImageThumbnail
                src={item.parameters?.main_logo_presigned_url}
                size={ImageThumbnailSizesEnum.Xxl}
              />
              <TableRow showTooltip content={item.name} />
            </div>
          );
        },
      },
      {
        id: 'description',
        sortable: true,
        title: t('Descripción'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow content={item.description || ''} />;
        },
      },
      {
        id: 'active_detection',
        sortable: true,
        title: t('Detección Imagen'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow title={item.active_detection ? t('SI') : t('NO')} />;
        },
      },
      {
        id: 'state',
        title: t('Estado'),
        sortable: true,
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <StateActiveInTable status={item.state} />;
        },
      },
      {
        id: 'updated_by',
        sortable: true,
        title: t('Actualizado por'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableUpdatedBy item={item} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDelete={() => onDelete?.(item)}
          />
        ),
      },
    ],
    [t, checkboxColumn, onSelect, onUpdate, onDelete]
  );

  return (
    <TablePaginated
      data={items}
      totalItems={totalItems}
      isFetching={isFetching}
      options={options}
      columns={columns}
      activeItem={activeItem}
      ignoreEmpty
    />
  );
}
